<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { Machine, ProductionOrder } from '../types/planning'

/**
 * Capacity and load on real data.
 *
 * The backend has no work-center table, calendar or standard hours, so this page
 * shows what does exist: machine state from `/production-execution/machines` and
 * the planned load per department from `/production-orders`. It deliberately
 * does not compute "available hours".
 */

const { t } = useI18n()
const { listMachines, listAllProductionOrders } = usePlanningApi()

const machines = ref<Machine[]>([])
const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')

const statusCounts = computed(() => ({
  running: machines.value.filter((machine) => machine.status === 'running').length,
  idle: machines.value.filter((machine) => machine.status === 'idle').length,
  down: machines.value.filter((machine) => machine.status === 'down').length,
  setup: machines.value.filter((machine) => machine.status === 'setup').length,
}))

const activeOrders = computed(() =>
  orders.value.filter((order) => order.status === 'in_progress' || order.status === 'planned'),
)

interface LoadRow {
  department: string
  orders: number
  planned: number
  produced: number
  percent: number
}

const loadByDepartment = computed<LoadRow[]>(() => {
  const grouped = new Map<string, LoadRow>()
  for (const order of activeOrders.value) {
    const key = order.department_name || '—'
    const row = grouped.get(key) ?? { department: key, orders: 0, planned: 0, produced: 0, percent: 0 }
    row.orders += 1
    row.planned += Number(order.planned_quantity ?? 0)
    row.produced += Number(order.produced_quantity ?? 0)
    grouped.set(key, row)
  }
  return [...grouped.values()]
    .map((row) => ({
      ...row,
      percent: row.planned > 0 ? Math.round((row.produced / row.planned) * 100) : 0,
    }))
    .sort((a, b) => b.planned - a.planned)
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [machineList, orderList] = await Promise.all([
      listMachines(),
      listAllProductionOrders({}),
    ])
    machines.value = machineList
    orders.value = orderList
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.capacity.title') }}</h2>
      <p>{{ t('planning.capacity.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="metric-grid">
      <section class="metric-card">
        <span class="metric-value">{{ statusCounts.running }}</span>
        <span class="metric-label">{{ t('planning.machineStatus.running') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ statusCounts.idle }}</span>
        <span class="metric-label">{{ t('planning.machineStatus.idle') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ statusCounts.down + statusCounts.setup }}</span>
        <span class="metric-label">{{ t('planning.capacity.machineState') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ activeOrders.length }}</span>
        <span class="metric-label">{{ t('planning.capacity.orders') }}</span>
      </section>
    </div>

    <section class="notice-panel">
      <strong>{{ t('planning.capacity.hoursGap') }}</strong>
      <span>{{ t('planning.capacity.hoursGapText') }}</span>
    </section>

    <section class="table-shell">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('planning.capacity.department') }}</th>
            <th class="cell-num">{{ t('planning.capacity.orders') }}</th>
            <th class="cell-num">{{ t('planning.capacity.plannedQuantity') }}</th>
            <th class="cell-num">{{ t('planning.common.produced') }}</th>
            <th>{{ t('planning.capacity.load') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in loadByDepartment" :key="row.department">
            <td class="cell-name">{{ row.department }}</td>
            <td class="cell-num">{{ row.orders }}</td>
            <td class="cell-num">{{ row.planned.toLocaleString() }}</td>
            <td class="cell-num">{{ row.produced.toLocaleString() }}</td>
            <td>
              <div class="load-cell">
                <span>{{ row.percent }}%</span>
                <div class="load-bar"><i :style="{ width: `${Math.min(row.percent, 100)}%` }" /></div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <p v-if="loadByDepartment.length === 0" class="empty-inline">{{ t('planning.capacity.noLoad') }}</p>
  </template>
</template>
